import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext();

// Send cookies on every request (HttpOnly session cookie is set by backend)
axios.defaults.withCredentials = true;

// sessionStorage is a FALLBACK for environments where the HttpOnly cookie
// cannot be sent back (e.g., cross-site iframe embedding blocks SameSite=None
// on some browsers, or ad-blockers strip cookies). Primary auth remains the
// HttpOnly cookie; the token is only read as a Bearer header if present.
const TOKEN_KEY = 'session_token';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(sessionStorage.getItem(TOKEN_KEY) || null);

  const getAuthHeaders = useCallback(() => {
    // Returns Bearer header ONLY when we have a token cached (fallback path).
    // When the HttpOnly cookie is working, no header is needed.
    const t = tokenRef.current;
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const storeToken = (token) => {
    tokenRef.current = token || null;
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  };

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/permissions`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setPermissions(res.data?.permissions || {});
    } catch {
      setPermissions(null);
    }
  }, [getAuthHeaders]);

  const hasPerm = useCallback((key) => {
    if (!user) return false;
    // Dono bypass — even if perms didn't load yet
    if (user.role === 'dono') return true;
    if (!permissions) return false;
    return !!permissions[key];
  }, [user, permissions]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setUser(res.data);
      fetchPermissions();
    } catch (err) { console.error('Failed to refresh user:', err); }
  }, [getAuthHeaders, fetchPermissions]);

  // Check existing session on load — cookie and/or Bearer fallback
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: getAuthHeaders(),
        });
        setUser(response.data);
        fetchPermissions();
      } catch {
        // If /me failed, drop any stale cached token so we don't loop
        storeToken(null);
        setUser(null);
        setPermissions(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, [getAuthHeaders, fetchPermissions]);

  const login = async (email, password, remember = false) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password, remember },
      { withCredentials: true }
    );
    // Cache the returned token as a sessionStorage fallback. The HttpOnly
    // cookie remains the primary auth when the browser accepts it.
    storeToken(response.data.session_token || null);
    setUser(response.data.user);
    fetchPermissions();
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (token, password) => {
    const response = await axios.post(`${API}/auth/reset-password`, { token, password });
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.put(
      `${API}/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { withCredentials: true, headers: getAuthHeaders() }
    );
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
    } catch (err) { console.error('Logout error:', err); }
    storeToken(null);
    setUser(null);
    setPermissions(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, forgotPassword, resetPassword, changePassword,
      logout, getAuthHeaders, refreshUser,
      permissions, hasPerm, refreshPermissions: fetchPermissions,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
